import { useState, useEffect, useCallback, useMemo } from "react";
import { axiosInstance, getCachedData, getApiErrorMessage } from "../App";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Camera, Calendar, Utensils, Edit, Trash2, Download, Users, Copy } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import ExportButton from "../components/common/ExportButton";
import DataExportButton from "../components/common/DataExportButton";
import SearchableChildSelect from "../components/common/SearchableChildSelect";
import { PageLayout } from "../components/layout/Sidebar";
import useFeaturePermissions from "../hooks/useFeaturePermissions";
import { useClasses } from "../hooks/useClasses";
import { useModuleAccess } from "../hooks/useModuleAccess";
import { LazyImage, optimizeCloudinaryUrl } from "../components/common/LazyImage";
import { FloatingElements, AnimatedHeader, AnimatedCard } from "../components/ui/AnimatedElements";
import CalendarWithDots from "../components/common/CalendarWithDots";
import { getLocalDateString } from "../lib/utils";
import { confirmIfBeforeNoon } from "../lib/timeUtils";
import AlphabetFilter from "../components/AlphabetFilter";
import StatisticsModal, { StatisticsSummary, MealTypeStatistics } from "../components/StatisticsModal";

// Helper function to download image
const downloadImage = async (imageUrl, filename = "gambar") => {
  try {
    toast.loading("Memuat turun gambar...", { id: "download" });
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${filename}_${getLocalDateString()}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    toast.success("Gambar berjaya dimuat turun!", { id: "download" });
  } catch (error) {
    console.error("Download error:", error);
    toast.error("Gagal memuat turun gambar", { id: "download" });
  }
};

export default function NutritionPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasAccess } = useFeaturePermissions(user?.role);
  const { isModuleOn } = useModuleAccess(user?.role);
  const showReferenceMenu = isModuleOn('nutrition_reference_menu');
  const [nutritionLogs, setNutritionLogs] = useState([]);
  const [children, setChildren] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [editingClassGroup, setEditingClassGroup] = useState(null); // For bulk edit by class
  const [statsRefreshKey, setStatsRefreshKey] = useState(0); // P3: trigger stats refresh
  const [selectedDate, setSelectedDate] = useState(() => searchParams.get('date') || getLocalDateString());
  const [selectedClass, setSelectedClass] = useState("all");
  const [nameFilter, setNameFilter] = useState(""); // NEW: For alphabet filter
  const [showStatsModal, setShowStatsModal] = useState(false); // NEW: For statistics modal
  const [statsData, setStatsData] = useState(null); // NEW: Statistics data
  const [selectedMealType, setSelectedMealType] = useState("all"); // NEW: Filter by meal type
  const [viewImageUrl, setViewImageUrl] = useState(null);
  const [addMode, setAddMode] = useState("individual"); // "individual" or "class"
  const [selectedClassForBulk, setSelectedClassForBulk] = useState("");
  const [excludedChildIds, setExcludedChildIds] = useState([]); // New: for excluding children
  const [excludeSearch, setExcludeSearch] = useState(""); // New: search for exclusion
  const [highlightedMenuId, setHighlightedMenuId] = useState(null); // For deep link highlighting
  
  // State untuk Duplicate Feature
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateMenu, setDuplicateMenu] = useState(null);
  const [duplicateMode, setDuplicateMode] = useState("individual");
  const [selectedChildrenForDuplicate, setSelectedChildrenForDuplicate] = useState([]);
  const [duplicating, setDuplicating] = useState(false);
  
  // State untuk Bulk Delete Dialog
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [bulkDeleteData, setBulkDeleteData] = useState(null); // {className, mealType, date, logs, childrenNames}
  const [bulkDeleting, setBulkDeleting] = useState(false);
  
  // State untuk Duplicate Warning Dialog (P1)
  const [showDupWarningDialog, setShowDupWarningDialog] = useState(false);
  const [dupWarningData, setDupWarningData] = useState(null);
  const [pendingFormData, setPendingFormData] = useState(null);
  
  // State untuk Menu Rujukan
  const [foodMenus, setFoodMenus] = useState([]);
  const [useReferenceMenu, setUseReferenceMenu] = useState(false);
  const [selectedMenuWeek, setSelectedMenuWeek] = useState("");
  const [selectedMenuDay, setSelectedMenuDay] = useState("");
  const [selectedMenuMeal, setSelectedMenuMeal] = useState("");
  const [loadingMenus, setLoadingMenus] = useState(false);
  
  // Meal types for filter
  const mealTypes = [
    { value: "all", label: "Semua Rutin" },
    { value: "Sarapan", label: "Sarapan" },
    { value: "Snek Pagi", label: "Snek Pagi" },
    { value: "Makan Tengah Hari", label: "Makan Tengah Hari" },
    { value: "Snek Petang", label: "Snek Petang" },
    { value: "Minum Susu", label: "Minum Susu" },
    { value: "Lain-lain", label: "Lain-lain" }
  ];
  
  // Handle deep link from notifications
  useEffect(() => {
    const dateParam = searchParams.get('date');
    const highlightParam = searchParams.get('highlight');
    
    if (dateParam) {
      setSelectedDate(dateParam);
    }
    if (highlightParam) {
      setHighlightedMenuId(highlightParam);
      // Clear highlight after 5 seconds
      setTimeout(() => setHighlightedMenuId(null), 5000);
      // Clear the search params after processing
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);
  
  // Save selectedClass to localStorage for StatisticsSummary component
  useEffect(() => {
    localStorage.setItem('selectedClass', selectedClass);
  }, [selectedClass]);
  
  const [formData, setFormData] = useState({
    child_id: "",
    meal_type: "",
    food_items: "",
    quantity: "",
    notes: "",
    time: "",
    date: getLocalDateString()
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [availableClasses, setAvailableClasses] = useState([]);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  // Classes list - hide Preschool for Guru (senarai dinamik dari DB)
  const { classes: dynamicClasses } = useClasses();
  const classes = useMemo(() => {
    const list = dynamicClasses || [];
    return user?.role === "guru"
      ? list.filter(c => !c.toLowerCase().startsWith("pre-school"))
      : list;
  }, [dynamicClasses, user?.role]);

  const fetchChildren = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/children');
      let childrenData = response.data;
      
      // Filter by branch first (for guru and admin)
      if (user?.role === "guru" || user?.role === "admin") {
        childrenData = childrenData.filter(c => c.branch === user.branch);
      }
      
      // For Guru - further filter to only show assigned classes
      if (user?.role === "guru") {
        const assignedClasses = user?.assigned_classes || [];
        if (assignedClasses.length > 0) {
          childrenData = childrenData.filter(c => assignedClasses.includes(c.class_name));
        }
        // Also hide Preschool children for Guru
        childrenData = childrenData.filter(c => c.class_name !== "Pre-school");
      }
      
      setChildren(childrenData);
      // Extract unique class names from filtered children data
      let uniqueClasses = [...new Set(childrenData.map(c => c.class_name).filter(Boolean))];
      // Hide Preschool for Guru
      if (user?.role === "guru") {
        uniqueClasses = uniqueClasses.filter(c => c !== "Pre-school");
      }
      setAvailableClasses(uniqueClasses.sort());
    } catch (error) {
      toast.error("Ralat mendapatkan data kanak-kanak");
    }
  }, [user]);

  const fetchNutrition = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/nutrition?date=${selectedDate}`);
      let logs = response.data;
      
      // Client-side filtering by name if needed
      if (nameFilter && children.length > 0) {
        const filteredChildren = children.filter(c => {
          if (nameFilter.length === 1) {
            return c.name.toLowerCase().startsWith(nameFilter.toLowerCase());
          } else {
            return c.name.toLowerCase().includes(nameFilter.toLowerCase());
          }
        });
        const childIds = filteredChildren.map(c => c.id);
        logs = logs.filter(log => childIds.includes(log.child_id));
      }
      
      setNutritionLogs(logs);
    } catch (error) {
      toast.error("Ralat mendapatkan data pemakanan");
    }
  }, [selectedDate, nameFilter, children]);

  // Fetch food reference menus
  const fetchFoodMenus = useCallback(async () => {
    setLoadingMenus(true);
    try {
      const response = await axiosInstance.get("/food-menu");
      setFoodMenus(response.data.menus || []);
    } catch (error) {
      console.error("Error fetching food menus:", error);
    } finally {
      setLoadingMenus(false);
    }
  }, []);

  // Fetch children and nutrition data (may re-run on filter changes)
  useEffect(() => {
    fetchChildren();
  }, [fetchChildren]);

  useEffect(() => {
    fetchNutrition();
  }, [fetchNutrition]);

  // Fetch food menus only once on mount - separate from other fetches to prevent re-loading glitch
  useEffect(() => {
    fetchFoodMenus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper: Get age group based on child's class
  const getAgeGroupFromClass = useCallback((className) => {
    if (!className) return "1-4 years";
    const classLower = className.toLowerCase();
    if (classLower.includes("baby") || classLower.includes("2-12")) return "6-11 months";
    if (classLower.includes("toddler i") || classLower.includes("1-2")) return "9-11 months";
    return "1-4 years"; // Default for toddler II, nursery, pre-school
  }, []);

  // Helper: Get menu options for selected age group
  const getMenuOptionsForAge = useCallback((ageGroup) => {
    return foodMenus.filter(menu => {
      const menuAge = menu.age_group.toLowerCase();
      if (ageGroup === "6-11 months") return menuAge.includes("6-11");
      if (ageGroup === "9-11 months") return menuAge.includes("9-11");
      return menuAge.includes("1-4");
    });
  }, [foodMenus]);

  // Helper: Get days from selected menu
  const getMenuDays = useCallback(() => {
    const menu = foodMenus.find(m => m.id === selectedMenuWeek);
    return menu?.days || [];
  }, [foodMenus, selectedMenuWeek]);

  // Helper: Get meals from selected day
  const getMenuMeals = useCallback(() => {
    const menu = foodMenus.find(m => m.id === selectedMenuWeek);
    const day = menu?.days?.find(d => d.day === selectedMenuDay);
    return day?.meals || [];
  }, [foodMenus, selectedMenuWeek, selectedMenuDay]);

  // Auto-fill form when menu meal is selected
  useEffect(() => {
    if (selectedMenuMeal && useReferenceMenu) {
      const meals = getMenuMeals();
      const meal = meals.find(m => m.meal_type === selectedMenuMeal);
      if (meal) {
        setFormData(prev => ({
          ...prev,
          meal_type: mapMealTypeToForm(meal.meal_type),
          food_items: meal.food_items,
          quantity: meal.other_info || ""
        }));
      }
    }
  }, [selectedMenuMeal, useReferenceMenu, getMenuMeals]);

  // Map menu meal types to form meal types
  const mapMealTypeToForm = (mealType) => {
    const mapping = {
      "Makan Tengahari": "Makan Tengah Hari",
      "Snek": "Snek Petang",
      "Minum Petang": "Snek Petang",
      "Sarapan": "Sarapan",
      "Makan Malam": "Lain-lain"
    };
    return mapping[mealType] || mealType;
  };

  // Use useMemo instead of useEffect + setState for filtering
  const filteredChildren = useMemo(() => {
    if (selectedClass === "all") {
      return children;
    }
    return children.filter(c => c.class_name === selectedClass);
  }, [selectedClass, children]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Warning if submitting before 12 PM
    if (!confirmIfBeforeNoon()) return;
    
    // Bulk EDIT for class group
    if (editingClassGroup && editingClassGroup.logs.length > 0) {
      setBulkSubmitting(true);
      
      let successCount = 0;
      let failCount = 0;
      
      for (const log of editingClassGroup.logs) {
        try {
          await axiosInstance.put(`/nutrition/${log.id}`, {
            ...formData,
            child_id: log.child_id // Keep original child_id
          });
          
          // Upload photo for each record if photo exists
          if (photoFile) {
            try {
              const photoFormData = new FormData();
              photoFormData.append('file', photoFile);
              await axiosInstance.post(`/nutrition/${log.id}/photo`, photoFormData, {
                headers: { 'Content-Type': 'multipart/form-data' }
              });
            } catch (photoError) {
              console.error(`Failed to upload photo for nutrition ${log.id}:`, photoError);
            }
          }
          
          successCount++;
        } catch (error) {
          failCount++;
        }
      }
      
      setBulkSubmitting(false);
      setPhotoFile(null); // Clear photo after upload
      
      if (successCount > 0) {
        toast.success(`${successCount} rekod menu berjaya dikemaskini untuk kelas ${editingClassGroup.className}!` + (photoFile ? ' (dengan gambar)' : ''));
      }
      if (failCount > 0) {
        toast.error(`${failCount} rekod menu gagal dikemaskini`);
      }
      
      setShowDialog(false);
      setEditingClassGroup(null);
      setFormData({
        child_id: "",
        meal_type: "",
        food_items: "",
        quantity: "",
        notes: "",
        time: "",
        date: getLocalDateString()
      });
      setAddMode("individual");
      setSelectedClassForBulk("");
      fetchNutrition();
      setStatsRefreshKey(k => k + 1);
      return;
    }
    
    // Bulk add for entire class or all classes
    if (addMode === "class" && (selectedClassForBulk || selectedClassForBulk === "all") && !editingLog && !editingClassGroup) {
      setBulkSubmitting(true);
      
      // Get children based on class selection
      let classChildren;
      if (selectedClassForBulk === "all") {
        classChildren = children;
      } else {
        classChildren = children.filter(c => c.class_name === selectedClassForBulk);
      }
      
      // Apply exclusions
      classChildren = classChildren.filter(c => !excludedChildIds.includes(c.id));
      
      if (classChildren.length === 0) {
        toast.error("Tiada kanak-kanak untuk ditambah rekod (semua dikecualikan atau kelas kosong)");
        setBulkSubmitting(false);
        return;
      }
      
      let successCount = 0;
      let failCount = 0;
      
      for (const child of classChildren) {
        try {
          const response = await axiosInstance.post('/nutrition', {
            ...formData,
            child_id: child.id
          });
          
          // Upload photo for each record if photo exists
          if (photoFile && response.data.id) {
            try {
              const photoFormData = new FormData();
              photoFormData.append('file', photoFile);
              await axiosInstance.post(`/nutrition/${response.data.id}/photo`, photoFormData, {
                headers: { 'Content-Type': 'multipart/form-data' }
              });
            } catch (photoError) {
              console.error(`Failed to upload photo for child ${child.id}:`, photoError);
            }
          }
          
          successCount++;
        } catch (error) {
          failCount++;
        }
      }
      
      setBulkSubmitting(false);
      
      const classLabel = selectedClassForBulk === "all" ? "semua kelas" : selectedClassForBulk;
      if (successCount > 0) {
        toast.success(`${successCount} rekod pemakanan berjaya ditambah untuk ${classLabel}!`);
      }
      if (failCount > 0) {
        toast.error(`${failCount} rekod gagal ditambah`);
      }
      
      setShowDialog(false);
      setFormData({
        child_id: "",
        meal_type: "",
        food_items: "",
        quantity: "",
        notes: "",
        time: "",
        date: getLocalDateString()
      });
      setPhotoFile(null);
      setAddMode("individual");
      setSelectedClassForBulk("");
      setExcludedChildIds([]);
      setExcludeSearch("");
      fetchNutrition();
      return;
    }
    
    // Individual add/edit
    try {
      let response;
      if (editingLog) {
        response = await axiosInstance.put(`/nutrition/${editingLog.id}`, formData);
        toast.success("Rekod pemakanan berjaya dikemaskini!");
      } else {
        try {
          response = await axiosInstance.post('/nutrition', formData);
          toast.success("Rekod pemakanan berjaya ditambah!");
        } catch (dupError) {
          if (dupError.response?.status === 409 && dupError.response?.data?.detail?.is_duplicate_warning) {
            setDupWarningData(dupError.response.data.detail);
            setPendingFormData({...formData});
            setShowDupWarningDialog(true);
            return;
          } else {
            throw dupError;
          }
        }
      }
      
      if (photoFile) {
        const photoFormData = new FormData();
        photoFormData.append('file', photoFile);
        await axiosInstance.post(`/nutrition/${response.data.id}/photo`, photoFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      setShowDialog(false);
      setEditingLog(null);
      setFormData({
        child_id: "",
        meal_type: "",
        food_items: "",
        quantity: "",
        notes: "",
        time: "",
        date: getLocalDateString()
      });
      setPhotoFile(null);
      fetchNutrition();
      setStatsRefreshKey(k => k + 1);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Ralat menyimpan rekod"));
    }
  };

  const handleEdit = (log) => {
    setEditingLog(log);
    setFormData({
      child_id: log.child_id,
      meal_type: log.meal_type,
      food_items: log.food_items,
      quantity: log.quantity || "",
      notes: log.notes || "",
      time: log.time,
      date: log.date
    });
    setShowDialog(true);
  };

  // Handle bulk edit for class group
  const handleBulkEditClass = (log, className) => {
    // Find all nutrition logs with same meal_type, time, date for this class
    const classLogs = nutritionLogs.filter(l => {
      const child = children.find(c => c.id === l.child_id);
      return child?.class_name === className && 
             l.meal_type === log.meal_type &&
             l.time === log.time &&
             l.date === log.date;
    });
    
    if (classLogs.length === 0) {
      toast.error("Tiada rekod menu untuk diedit");
      return;
    }
    
    setEditingClassGroup({
      logs: classLogs,
      className: className,
      originalData: log
    });
    
    setFormData({
      child_id: "", // Not needed for class edit
      meal_type: log.meal_type,
      food_items: log.food_items,
      quantity: log.quantity || "",
      notes: log.notes || "",
      time: log.time,
      date: log.date
    });
    setAddMode("class");
    setSelectedClassForBulk(className);
    setShowDialog(true);
  };

  // Handle bulk delete for class group - Show confirmation dialog first
  const handleBulkDeleteClass = async (log, className) => {
    // Find all matching logs for this class, meal type, and date
    const classLogs = nutritionLogs.filter(l => {
      const child = children.find(c => c.id === l.child_id);
      const matchClass = child?.class_name === className;
      const matchMealType = l.meal_type === log.meal_type;
      const matchDate = l.date === log.date;
      return matchClass && matchMealType && matchDate;
    });
    
    if (classLogs.length === 0) {
      toast.error("Tiada rekod menu untuk dipadam");
      return;
    }
    
    // Get children names for display
    const childrenNames = classLogs.map(l => {
      const child = children.find(c => c.id === l.child_id);
      return child?.name || 'Unknown';
    });
    
    // Show detailed confirmation dialog
    setBulkDeleteData({
      className,
      mealType: log.meal_type,
      date: log.date,
      logs: classLogs,
      childrenNames: [...new Set(childrenNames)], // Unique names
      time: log.time
    });
    setShowBulkDeleteDialog(true);
  };
  
  // Execute bulk delete after confirmation
  const executeBulkDelete = async () => {
    if (!bulkDeleteData) return;
    
    setBulkDeleting(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const l of bulkDeleteData.logs) {
      try {
        await axiosInstance.delete(`/nutrition/${l.id}`);
        successCount++;
      } catch (error) {
        console.error(`[BulkDelete] Failed to delete log ${l.id}:`, error);
        failCount++;
      }
    }
    
    setBulkDeleting(false);
    setShowBulkDeleteDialog(false);
    setBulkDeleteData(null);
    
    if (successCount > 0) {
      toast.success(`✅ ${successCount} rekod menu berjaya dipadam!`);
    }
    if (failCount > 0) {
      toast.error(`❌ ${failCount} rekod menu gagal dipadam`);
    }
    
    fetchNutrition();
  };

  const handleDelete = async (logId) => {
    if (!window.confirm("Adakah anda pasti mahu memadam rekod pemakanan ini?")) return;
    
    try {
      await axiosInstance.delete(`/nutrition/${logId}`);
      toast.success("Rekod pemakanan berjaya dipadam!");
      fetchNutrition();
      setStatsRefreshKey(k => k + 1);
    } catch (error) {
      toast.error("Ralat memadam rekod pemakanan");
    }
  };

  // Handle Duplicate Menu
  const handleOpenDuplicate = (log) => {
    setDuplicateMenu(log);
    setDuplicateMode("individual");
    setSelectedChildrenForDuplicate([]);
    setShowDuplicateDialog(true);
  };

  // Handle Duplicate Warning Confirm (P1)
  const handleDupWarningConfirm = async () => {
    if (!pendingFormData) return;
    try {
      const response = await axiosInstance.post('/nutrition?force=true', pendingFormData);
      toast.success("Rekod pemakanan berjaya ditambah (duplikat dikonfirm)!");
      
      if (photoFile && response.data?.id) {
        const pfd = new FormData();
        pfd.append('file', photoFile);
        await axiosInstance.post(`/nutrition/${response.data.id}/photo`, pfd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      setShowDupWarningDialog(false);
      setDupWarningData(null);
      setPendingFormData(null);
      setShowDialog(false);
      setEditingLog(null);
      setFormData({ child_id: "", meal_type: "", food_items: "", quantity: "", notes: "", time: "", date: getLocalDateString() });
      setPhotoFile(null);
      fetchNutrition();
      setStatsRefreshKey(k => k + 1);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Ralat menyimpan rekod"));
    }
  };

  const handleDupWarningCancel = () => {
    setShowDupWarningDialog(false);
    setDupWarningData(null);
    setPendingFormData(null);
  };

  const handleDuplicateMenu = async () => {
    if (!duplicateMenu) return;
    
    setDuplicating(true);
    try {
      const childIds = duplicateMode === "individual" 
        ? [duplicateMenu.child_id]
        : selectedChildrenForDuplicate;
      
      if (childIds.length === 0) {
        toast.error("Sila pilih sekurang-kurangnya satu kanak-kanak");
        setDuplicating(false);
        return;
      }

      let successCount = 0;
      let errorCount = 0;

      for (const childId of childIds) {
        try {
          await axiosInstance.post('/nutrition?force=true', {
            child_id: childId,
            date: duplicateMenu.date,
            meal_type: duplicateMenu.meal_type,
            food_items: duplicateMenu.food_items,
            quantity: duplicateMenu.quantity || "",
            portion_eaten: duplicateMenu.portion_eaten || "",
            time: duplicateMenu.time || "",
            notes: duplicateMenu.notes || "",
            photo_url: duplicateMenu.photo_url || ""
          });
          successCount++;
        } catch (err) {
          errorCount++;
          console.error(`Failed to duplicate for child ${childId}:`, err);
        }
      }

      if (successCount > 0) {
        toast.success(`Berjaya duplicate ${successCount} rekod pemakanan!`);
        fetchNutrition();
        setStatsRefreshKey(k => k + 1);
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} rekod gagal di-duplicate`);
      }

      setShowDuplicateDialog(false);
      setDuplicateMenu(null);
      setSelectedChildrenForDuplicate([]);
    } catch (error) {
      toast.error("Ralat semasa duplicate rekod pemakanan");
    } finally {
      setDuplicating(false);
    }
  };

  const toggleChildForDuplicate = (childId) => {
    setSelectedChildrenForDuplicate(prev => 
      prev.includes(childId) 
        ? prev.filter(id => id !== childId)
        : [...prev, childId]
    );
  };

  const selectAllChildrenForDuplicate = (className) => {
    const classChildren = children.filter(c => c.class_name === className);
    const classChildIds = classChildren.map(c => c.id);
    setSelectedChildrenForDuplicate(prev => {
      const newSelection = [...prev];
      classChildIds.forEach(id => {
        if (!newSelection.includes(id)) {
          newSelection.push(id);
        }
      });
      return newSelection;
    });
  };

  // mealTypes moved to top with filter options

  return (
    <PageLayout user={user} onLogout={onLogout} hasAccess={hasAccess}>
      <FloatingElements variant="default" />
      <div className="max-w-6xl mx-auto relative z-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <AnimatedHeader 
            emoji="🍽️"
            title="Rutin Makan"
            subtitle="🥗 Rekod pemakanan harian kanak-kanak"
            delay={100}
          />
          <div className="flex items-center gap-2 flex-wrap">
            {(user.role === 'admin' || user.role === 'guru') && (
              <DataExportButton 
                dataType="nutrition"
                childrenList={children}
                classes={availableClasses}
                title="Export"
              />
            )}
            {(user.role === 'admin' || user.role === 'guru') && (
              <Button
                data-testid="add-nutrition-button"
                onClick={() => setShowDialog(true)}
                className="bg-gradient-to-r from-green-400 to-emerald-500 hover:from-green-500 hover:to-emerald-600 text-white rounded-2xl font-black shadow-lg hover:shadow-xl hover:scale-105 transition-all text-sm px-3 py-2"
              >
                <Plus className="w-4 h-4 mr-1" />
                Tambah
              </Button>
            )}
          </div>
        </div>

        {/* Date Filter - Space Explorer Style */}
        <AnimatedCard className="mb-6" delay={200} color="green">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center shadow-lg">
              <span className="text-2xl">📅</span>
            </div>
            <div className="flex-1">
              <label className="text-sm font-black text-gray-600 mb-1 block">Pilih Tarikh</label>
              <div className="flex items-center gap-3">
                <div 
                  data-testid="date-display"
                  className="flex-1 px-4 py-3 border-3 border-green-200 rounded-2xl bg-white font-bold text-gray-700"
                >
                  {new Date(selectedDate).toLocaleDateString('ms-MY', { 
                    weekday: 'long', 
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric' 
                  })}
                </div>
                <CalendarWithDots 
                  dataType="nutrition"
                  selectedDate={selectedDate}
                  onDateChange={setSelectedDate}
                  dotColor="bg-green-500"
                  user={user}
                />
              </div>
            </div>
          </div>
        </AnimatedCard>

        {/* Class Filter - Space Explorer Style */}
        {(user.role === "admin" || user.role === "guru" || user.role === "superadmin") && availableClasses.length > 0 && isModuleOn("filter_class_nutrition") && (
          <AnimatedCard className="mb-6" delay={300} color="orange">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center shadow-lg">
                <span className="text-xl">🏫</span>
              </div>
              <h3 className="font-black text-gray-700">Pilih Mengikut Kelas</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedClass("all")}
                className={`px-4 py-2.5 rounded-2xl font-bold transition-all duration-300 text-sm ${
                  selectedClass === "all"
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-xl scale-105'
                    : 'bg-gray-100 text-gray-600 hover:bg-green-100 hover:scale-105'
                }`}
              >
                🍽️ Semua ({new Set(nutritionLogs.map(log => log.child_id)).size})
              </button>
              {availableClasses.map((className, idx) => {
                // Count unique children with nutrition logs for this class
                const classChildCount = new Set(nutritionLogs.filter(log => {
                  const child = children.find(c => c.id === log.child_id);
                  return child?.class_name === className;
                }).map(log => log.child_id)).size;
                return (
                  <button
                    key={className}
                    onClick={() => setSelectedClass(className)}
                    className={`px-4 py-2.5 rounded-2xl font-bold transition-all duration-300 text-sm ${
                      selectedClass === className
                        ? 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white shadow-xl scale-105'
                        : 'bg-gray-100 text-gray-600 hover:bg-teal-100 hover:scale-105'
                    }`}
                  >
                    {['🍼', '🧸', '🎨', '🌈', '📚'][idx % 5]} {className.split('(')[0].trim()} ({classChildCount})
                  </button>
                );
              })}
            </div>
          </AnimatedCard>
        )}


        {/* Alphabet Filter & Statistics - NEW FEATURES */}
        {(user.role === "admin" || user.role === "guru" || user.role === "superadmin") && (
          <>
            {isModuleOn("filter_name_nutrition") && (
            <AnimatedCard className="mb-6" delay={350} color="blue">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center shadow-lg">
                  <span className="text-xl">🔤</span>
                </div>
                <h3 className="font-black text-gray-700">Cari Mengikut Nama</h3>
              </div>
              <AlphabetFilter
                onFilterChange={(filter) => {
                  if (filter.type === 'all') {
                    setNameFilter('');
                  } else {
                    setNameFilter(filter.value);
                  }
                }}
              />
            </AnimatedCard>
            )}

            {/* Statistics Summary - P2: Per Meal Type Breakdown */}
            {selectedClass !== 'all' && (
              <AnimatedCard className="mb-6" delay={380} color="purple">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center shadow-lg">
                    <span className="text-xl">📊</span>
                  </div>
                  <h3 className="font-black text-gray-700">Status Kemaskini Menu</h3>
                  <StatisticsSummary
                    className={selectedClass}
                    date={selectedDate}
                    type="menu"
                    refreshKey={statsRefreshKey}
                    onOpenModal={(stats) => {
                      setStatsData(stats);
                      setShowStatsModal(true);
                    }}
                  />
                </div>
                <MealTypeStatistics
                  date={selectedDate}
                  refreshKey={statsRefreshKey}
                  className={selectedClass}
                  onOpenModal={(stats, mealTypeName) => {
                    setStatsData({...stats, mealTypeName});
                    setShowStatsModal(true);
                  }}
                />
              </AnimatedCard>
            )}
          </>
        )}


        {/* Meal Type Filter - Space Explorer Style (Admin & Guru only) */}
        {(user.role === "admin" || user.role === "guru" || user.role === "superadmin") && isModuleOn("filter_meal_nutrition") && (
          <AnimatedCard className="mb-6" delay={350} color="pink">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center shadow-lg">
                <span className="text-xl">🍽️</span>
              </div>
              <h3 className="font-black text-gray-700">Pilih Mengikut Rutin Makan</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {mealTypes.map((meal) => {
                // Count unique children for this meal type (filtered by selected class)
                const classFilteredLogs = selectedClass === "all" 
                  ? nutritionLogs 
                  : nutritionLogs.filter(log => {
                      const child = children.find(c => c.id === log.child_id);
                      return child?.class_name === selectedClass;
                    });
                const mealChildCount = meal.value === "all" 
                  ? new Set(classFilteredLogs.map(log => log.child_id)).size
                  : new Set(classFilteredLogs.filter(log => log.meal_type === meal.value).map(log => log.child_id)).size;
                
                // Emoji for each meal type
                const mealEmoji = {
                  "all": "🍽️",
                  "Sarapan": "🌅",
                  "Snek Pagi": "🍪",
                  "Makan Tengah Hari": "🍱",
                  "Snek Petang": "🧁",
                  "Minum Susu": "🍼",
                  "Lain-lain": "🥗"
                }[meal.value] || "🍽️";
                
                return (
                  <button
                    key={meal.value}
                    onClick={() => setSelectedMealType(meal.value)}
                    className={`px-4 py-2.5 rounded-2xl font-bold transition-all duration-300 text-sm ${
                      selectedMealType === meal.value
                        ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-xl scale-105'
                        : 'bg-gray-100 text-gray-600 hover:bg-pink-100 hover:scale-105'
                    }`}
                    data-testid={`meal-filter-${meal.value}`}
                  >
                    {mealEmoji} {meal.label} ({mealChildCount})
                  </button>
                );
              })}
            </div>
          </AnimatedCard>
        )}

        {/* Nutrition Logs List */}
        <div className="space-y-4">
            {nutritionLogs
              .filter(log => {
                // Filter by class
                if (selectedClass !== "all") {
                  const child = children.find(c => c.id === log.child_id);
                  if (child?.class_name !== selectedClass) return false;
                }
                // Filter by meal type
                if (selectedMealType !== "all") {
                  if (log.meal_type !== selectedMealType) return false;
                }
                return true;
              })
              .map((log, idx) => {
              const child = children.find(c => c.id === log.child_id);
              const hasAllergy = child?.allergies;
              const isHighlighted = highlightedMenuId === log.id;
              return (
                <AnimatedCard
                  key={log.id}
                  data-testid={`nutrition-card-${log.id}`}
                  className={`hover:scale-[1.01] !p-5 ${isHighlighted ? 'ring-4 ring-pink-400 ring-offset-2 animate-pulse' : ''}`}
                  delay={400 + (idx * 50)}
                  color={['green', 'teal', 'emerald', 'lime'][idx % 4]}
                >
                  <div className="flex flex-col sm:flex-row gap-4">
                    {log.photo_url && (
                      <div 
                        className="w-full sm:w-28 h-28 rounded-2xl overflow-hidden flex-shrink-0 cursor-pointer group relative shadow-lg"
                        onClick={() => setViewImageUrl(log.photo_url)}
                        title="Klik untuk lihat gambar besar"
                      >
                        {/* Animated gradient border */}
                        <div className="absolute inset-0 bg-gradient-to-r from-green-400 via-emerald-400 to-teal-400 rounded-2xl animate-pulse opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ padding: '3px' }}>
                          <div className="w-full h-full bg-white rounded-xl"></div>
                        </div>
                        <div className="relative w-full h-full transform transition-all duration-300 group-hover:scale-105">
                          <LazyImage
                            src={log.photo_url}
                            alt="Food"
                            className="w-full h-full object-cover rounded-2xl"
                            fallbackColor="bg-gradient-to-br from-green-200 to-emerald-200"
                          />
                          {/* Hover overlay with zoom icon */}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 rounded-2xl flex items-center justify-center">
                            <span className="text-white text-2xl opacity-0 group-hover:opacity-100 transform scale-50 group-hover:scale-100 transition-all duration-300">🔍</span>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xl">🍽️</span>
                            <h3 className="font-bold text-lg text-gray-800 truncate">{log.meal_type}</h3>
                          </div>
                          <p className="text-sm text-green-600 font-semibold truncate">{log.child_name || child?.name || 'Unknown'}</p>
                          <p className="text-xs text-gray-500 truncate">{child?.class_name || ''}</p>
                          {hasAllergy && (
                            <p className="text-xs text-red-600 mt-1 truncate">⚠️ Alahan: {hasAllergy}</p>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className="px-3 py-1.5 bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 rounded-full text-sm font-bold border border-green-200 shadow-sm whitespace-nowrap">
                            🕐 {(() => {
                              // Format time with AM/PM
                              if (!log.time) return '-';
                              const [hours, minutes] = log.time.split(':');
                              const hour = parseInt(hours);
                              const ampm = hour >= 12 ? 'PM' : 'AM';
                              const hour12 = hour % 12 || 12;
                              return `${hour12}:${minutes} ${ampm}`;
                            })()}
                          </span>
                          {(user.role === 'admin' || user.role === 'guru') && (
                            <div className="flex flex-wrap gap-1">
                              {/* Duplicate Button */}
                              <button
                                onClick={() => handleOpenDuplicate(log)}
                                className="p-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
                                title="Duplicate rekod pemakanan"
                              >
                                <Copy className="w-4 h-4" />
                              </button>
                              {/* Individual Edit/Delete */}
                              <button
                                onClick={() => handleEdit(log)}
                                className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                                title="Edit individu"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(log.id)}
                                className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                                title="Padam individu"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                              {/* Class Edit/Delete - only show if child has class */}
                              {child?.class_name && (
                                <>
                                  <button
                                    onClick={() => handleBulkEditClass(log, child.class_name)}
                                    className="p-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors flex items-center gap-1"
                                    title={`Edit semua ${log.meal_type} untuk kelas ${child.class_name}`}
                                  >
                                    <Users className="w-4 h-4" />
                                    <Edit className="w-3 h-3" />
                                  </button>
                                  <button
                                    onClick={() => handleBulkDeleteClass(log, child.class_name)}
                                    className="p-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors flex items-center gap-1"
                                    title={`Padam semua ${log.meal_type} untuk kelas ${child.class_name}`}
                                  >
                                    <Users className="w-4 h-4" />
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Food details */}
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-3 border border-green-100">
                        <p className="text-gray-700 text-sm"><span className="font-bold text-green-700">🍴 Menu:</span> {log.food_items}</p>
                        {log.quantity && (
                          <p className="text-sm text-gray-600 mt-1"><span className="font-bold text-green-700">🥤 Air Minuman:</span> {log.quantity}</p>
                        )}
                        {log.notes && (
                          <p className="text-sm text-gray-600 italic mt-1"><span className="font-bold text-green-700">📝 Nota:</span> {log.notes}</p>
                        )}
                        {/* Teacher Name • Timestamp - hidden for ibu_bapa */}
                        {user.role !== 'ibu_bapa' && (log.teacher_name || log.created_at) && (
                          <p className="text-xs text-gray-400 mt-2 pt-2 border-t border-green-100">
                            {log.teacher_name && <span>{log.teacher_name}</span>}
                            {log.teacher_name && log.created_at && <span> • </span>}
                            {log.created_at && (
                              <span>{new Date(log.created_at).toLocaleString('ms-MY', { 
                                day: 'numeric', 
                                month: 'short', 
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}</span>
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </AnimatedCard>
              );
            })}
          </div>

          {nutritionLogs.length === 0 && (
            <div data-testid="no-nutrition-message" className="text-center py-16 px-6">
              {/* Decorative background */}
              <div className="relative inline-block mb-4">
                <div className="absolute inset-0 w-28 h-28 bg-gradient-to-br from-orange-200 to-amber-200 rounded-full blur-xl opacity-50"></div>
                <div className="relative w-24 h-24 bg-gradient-to-br from-orange-100 to-amber-100 rounded-full flex items-center justify-center mx-auto">
                  <Utensils className="w-12 h-12 text-orange-400" />
                </div>
              </div>
              <h3 className="text-lg font-bold text-gray-700 mb-2">Tiada Rekod Pemakanan</h3>
              <p className="text-gray-500 text-sm mb-1">Tiada rekod pemakanan untuk tarikh ini</p>
              <p className="text-xs text-gray-400">Cuba pilih tarikh lain atau tunggu guru merekod menu</p>
            </div>
          )}
        </div>

      <Dialog open={showDialog} onOpenChange={(open) => {
        setShowDialog(open);
        if (!open) {
          setEditingLog(null);
          setEditingClassGroup(null);
          setFormData({
            child_id: "",
            meal_type: "",
            food_items: "",
            quantity: "",
            notes: "",
            time: "",
            date: getLocalDateString()
          });
          setPhotoFile(null);
          setAddMode("individual");
          setSelectedClassForBulk("");
          setExcludedChildIds([]);
          setExcludeSearch("");
        }
      }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingClassGroup 
                ? `Edit Menu Kelas ${editingClassGroup.className} (${editingClassGroup.logs.length} rekod)` 
                : editingLog 
                  ? "Edit Rekod Pemakanan" 
                  : "Tambah Rekod Pemakanan"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            
            {/* Add Mode Toggle - Only show when adding new */}
            {!editingLog && !editingClassGroup && (user.role === 'admin' || user.role === 'guru') && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Mod Tambah</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setAddMode("individual")}
                    className={`flex-1 py-2 px-4 rounded-xl font-medium text-sm transition-all ${
                      addMode === "individual"
                        ? 'bg-gradient-to-r from-green-400 to-green-500 text-white shadow-lg'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    👤 Individu
                  </button>
                  <button
                    type="button"
                    onClick={() => setAddMode("class")}
                    className={`flex-1 py-2 px-4 rounded-xl font-medium text-sm transition-all ${
                      addMode === "class"
                        ? 'bg-gradient-to-r from-green-400 to-green-500 text-white shadow-lg'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    👥 Ikut Kelas
                  </button>
                </div>
              </div>
            )}

            {/* Individual Mode - Child Select */}
            {addMode === "individual" && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Kanak-kanak</label>
                <SearchableChildSelect
                  data-testid="nutrition-child-select"
                  childrenList={user.role === "admin" || user.role === "guru" ? filteredChildren : children}
                  value={formData.child_id}
                  onChange={(childId) => setFormData({...formData, child_id: childId})}
                  placeholder="Cari atau pilih kanak-kanak..."
                />
              </div>
            )}

            {/* Class Mode - Class Select */}
            {addMode === "class" && !editingLog && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Pilih Kelas</label>
                  <select
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-400 focus:ring-2 focus:ring-green-200 transition-all outline-none"
                    value={selectedClassForBulk}
                    onChange={(e) => {
                      setSelectedClassForBulk(e.target.value);
                      setExcludedChildIds([]);
                    }}
                    required={addMode === "class"}
                  >
                    <option value="">Pilih kelas</option>
                    <option value="all">🌟 Semua Kelas ({children.length} kanak-kanak)</option>
                    {availableClasses.map(className => {
                      const count = children.filter(c => c.class_name === className).length;
                      return (
                        <option key={className} value={className} disabled={count === 0}>
                          {className} ({count} kanak-kanak)
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* Exclusion Section */}
                {selectedClassForBulk && (
                  <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
                    <label className="block text-sm font-semibold text-orange-700 mb-2">
                      🚫 Kecualikan Kanak-kanak (Pilihan)
                    </label>
                    <p className="text-xs text-orange-600 mb-3">
                      Cari dan pilih kanak-kanak yang tidak perlu menerima rekod ini
                    </p>
                    
                    {/* Search input */}
                    <div className="relative mb-3">
                      <input
                        type="text"
                        placeholder="Cari nama untuk dikecualikan..."
                        value={excludeSearch}
                        onChange={(e) => setExcludeSearch(e.target.value)}
                        className="w-full px-4 py-2 pl-10 border border-orange-200 rounded-lg text-sm focus:border-orange-400 focus:ring-1 focus:ring-orange-200"
                      />
                      <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    
                    {/* Children list for exclusion */}
                    <div className="max-h-40 overflow-y-auto space-y-1 bg-white rounded-lg p-2">
                      {(() => {
                        const targetChildren = selectedClassForBulk === "all" 
                          ? children 
                          : children.filter(c => c.class_name === selectedClassForBulk);
                        
                        const searchedChildren = excludeSearch 
                          ? targetChildren.filter(c => 
                              c.name?.toLowerCase().includes(excludeSearch.toLowerCase()) ||
                              c.nickname?.toLowerCase().includes(excludeSearch.toLowerCase())
                            )
                          : targetChildren;
                        
                        return searchedChildren.map(child => (
                          <label 
                            key={child.id} 
                            className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                              excludedChildIds.includes(child.id) 
                                ? 'bg-red-100 text-red-700' 
                                : 'hover:bg-gray-100'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={excludedChildIds.includes(child.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setExcludedChildIds([...excludedChildIds, child.id]);
                                } else {
                                  setExcludedChildIds(excludedChildIds.filter(id => id !== child.id));
                                }
                              }}
                              className="w-4 h-4 text-red-500 border-gray-300 rounded focus:ring-red-400"
                            />
                            <span className={`text-sm ${excludedChildIds.includes(child.id) ? 'line-through' : ''}`}>
                              {child.name}
                            </span>
                            <span className="text-xs text-gray-400">
                              {selectedClassForBulk === "all" ? child.class_name : ''}
                            </span>
                          </label>
                        ));
                      })()}
                    </div>
                    
                    {/* Exclusion summary */}
                    {excludedChildIds.length > 0 && (
                      <div className="mt-2 flex items-center justify-between">
                        <p className="text-xs text-red-600">
                          {excludedChildIds.length} kanak-kanak dikecualikan
                        </p>
                        <button
                          type="button"
                          onClick={() => setExcludedChildIds([])}
                          className="text-xs text-red-600 hover:text-red-800 underline"
                        >
                          Reset
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Summary */}
                {selectedClassForBulk && (
                  <p className="text-xs text-green-600">
                    ✓ {(() => {
                      const targetChildren = selectedClassForBulk === "all" 
                        ? children 
                        : children.filter(c => c.class_name === selectedClassForBulk);
                      return targetChildren.length - excludedChildIds.length;
                    })()} kanak-kanak akan menerima rekod ini
                  </p>
                )}
              </div>
            )}

            {/* Toggle Menu Rujukan (kawalan module) */}
            {showReferenceMenu && (
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-4 border border-orange-200">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useReferenceMenu}
                  onChange={(e) => {
                    setUseReferenceMenu(e.target.checked);
                    if (!e.target.checked) {
                      setSelectedMenuWeek("");
                      setSelectedMenuDay("");
                      setSelectedMenuMeal("");
                    }
                  }}
                  className="w-5 h-5 rounded-lg text-orange-500 focus:ring-orange-300"
                />
                <div>
                  <span className="font-semibold text-orange-700">📋 Guna Menu Rujukan</span>
                  <p className="text-xs text-orange-600">Isi automatik dari jadual menu mingguan</p>
                </div>
              </label>
              
              {useReferenceMenu && (
                <div className="mt-4 space-y-3">
                  {loadingMenus ? (
                    <p className="text-sm text-orange-600 animate-pulse">Memuatkan menu...</p>
                  ) : (
                    <>
                      {/* Pilih Minggu/Menu */}
                      <div>
                        <label className="block text-xs font-semibold text-orange-700 mb-1">1️⃣ Pilih Minggu & Umur</label>
                        <select
                          className="w-full px-3 py-2 border border-orange-200 rounded-lg text-sm focus:border-orange-400 focus:ring-1 focus:ring-orange-200"
                          value={selectedMenuWeek}
                          onChange={(e) => {
                            setSelectedMenuWeek(e.target.value);
                            setSelectedMenuDay("");
                            setSelectedMenuMeal("");
                          }}
                        >
                          <option value="">Pilih minggu...</option>
                          {foodMenus.map(menu => (
                            <option key={menu.id} value={menu.id}>
                              {menu.week} - {menu.age_group} ({menu.title})
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      {/* Pilih Hari */}
                      {selectedMenuWeek && (
                        <div>
                          <label className="block text-xs font-semibold text-orange-700 mb-1">2️⃣ Pilih Hari</label>
                          <select
                            className="w-full px-3 py-2 border border-orange-200 rounded-lg text-sm focus:border-orange-400 focus:ring-1 focus:ring-orange-200"
                            value={selectedMenuDay}
                            onChange={(e) => {
                              setSelectedMenuDay(e.target.value);
                              setSelectedMenuMeal("");
                            }}
                          >
                            <option value="">Pilih hari...</option>
                            {getMenuDays().map(day => (
                              <option key={day.day} value={day.day}>
                                {day.day} ({day.meals?.length || 0} hidangan)
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      
                      {/* Pilih Hidangan */}
                      {selectedMenuDay && (
                        <div>
                          <label className="block text-xs font-semibold text-orange-700 mb-1">3️⃣ Pilih Hidangan</label>
                          <div className="space-y-2">
                            {getMenuMeals().map((meal, idx) => (
                              <button
                                key={idx}
                                type="button"
                                onClick={() => setSelectedMenuMeal(meal.meal_type)}
                                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                                  selectedMenuMeal === meal.meal_type
                                    ? 'bg-orange-500 text-white'
                                    : 'bg-white border border-orange-200 hover:bg-orange-100'
                                }`}
                              >
                                <span className="font-semibold">{meal.meal_type}</span>
                                <p className="text-xs opacity-80 truncate">{meal.food_items}</p>
                                {meal.other_info && (
                                  <p className="text-xs opacity-60">🥛 {meal.other_info}</p>
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Preview */}
                      {selectedMenuMeal && (
                        <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                          <p className="text-xs font-semibold text-green-700 mb-1">✅ Menu terpilih akan diisi ke form</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Rutin Makan</label>
              <select
                data-testid="nutrition-meal-type-select"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-400 focus:ring-2 focus:ring-green-200 transition-all outline-none"
                value={formData.meal_type}
                onChange={(e) => setFormData({...formData, meal_type: e.target.value})}
                required
              >
                <option value="">Pilih rutin makan</option>
                {mealTypes.filter(m => m.value !== "all").map(meal => (
                  <option key={meal.value} value={meal.value}>{meal.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Nama Menu</label>
              <textarea
                data-testid="nutrition-food-items-input"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-400 focus:ring-2 focus:ring-green-200 transition-all outline-none"
                value={formData.food_items}
                onChange={(e) => setFormData({...formData, food_items: e.target.value})}
                required
                rows={2}
                placeholder="Contoh: Nasi, ayam, sayur, buah-buahan"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Air Minuman</label>
              <input
                data-testid="nutrition-quantity-input"
                type="text"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-400 focus:ring-2 focus:ring-green-200 transition-all outline-none"
                value={formData.quantity}
                onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                placeholder="Contoh: susu, susu coklat, air anggur dll"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Masa</label>
              <input
                data-testid="nutrition-time-input"
                type="time"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-400 focus:ring-2 focus:ring-green-200 transition-all outline-none"
                value={formData.time}
                onChange={(e) => setFormData({...formData, time: e.target.value})}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Tarikh</label>
              <input
                data-testid="nutrition-date-input"
                type="date"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-400 focus:ring-2 focus:ring-green-200 transition-all outline-none"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Gambar (Pilihan)</label>
              <div className="flex items-center gap-3">
                <label className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl hover:border-green-400 cursor-pointer transition-all">
                  <Camera className="w-5 h-5 text-gray-400" />
                  <span className="text-sm text-gray-600">
                    {photoFile ? photoFile.name : 'Pilih gambar'}
                  </span>
                  <input
                    data-testid="nutrition-photo-input"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => setPhotoFile(e.target.files[0])}
                  />
                </label>
              </div>
            </div>

            <Button
              data-testid="submit-nutrition-button"
              type="submit"
              disabled={bulkSubmitting}
              className="w-full bg-gradient-to-r from-green-400 to-green-500 hover:from-green-500 hover:to-green-600 text-white"
            >
              {bulkSubmitting ? "Menambah rekod..." : 
               editingLog ? "Kemaskini Rekod" : 
               addMode === "class" ? `Tambah untuk Semua (${children.filter(c => c.class_name === selectedClassForBulk).length} kanak-kanak)` : 
               "Tambah Rekod"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Duplicate Warning Dialog (P1) */}
      <Dialog open={showDupWarningDialog} onOpenChange={(open) => {
        if (!open) handleDupWarningCancel();
      }}>
        <DialogContent className="max-w-md" data-testid="nutrition-duplicate-warning-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-600 flex items-center gap-2">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              Amaran Duplikat Dikesan
            </DialogTitle>
          </DialogHeader>
          
          {dupWarningData && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-gray-700 font-medium mb-2">{dupWarningData.message}</p>
                {dupWarningData.child_name && (
                  <p className="text-sm text-gray-500">Kanak-kanak: <strong>{dupWarningData.child_name}</strong></p>
                )}
                {dupWarningData.existing_food && (
                  <p className="text-sm text-gray-500 mt-1">Menu sedia ada: <em>{dupWarningData.existing_food}</em></p>
                )}
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                <p className="text-xs text-blue-700">
                  Jika anda teruskan, notifikasi amaran akan dihantar kepada Admin, Pengurus Cawangan dan Superadmin.
                </p>
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={handleDupWarningCancel}
                  className="flex-1"
                  data-testid="nutrition-dup-cancel-btn"
                >
                  Batal
                </Button>
                <Button
                  onClick={handleDupWarningConfirm}
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                  data-testid="nutrition-dup-confirm-btn"
                >
                  Teruskan
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={showBulkDeleteDialog} onOpenChange={(open) => {
        if (!open && !bulkDeleting) {
          setShowBulkDeleteDialog(false);
          setBulkDeleteData(null);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Pengesahan Padam Menu Kelas
            </DialogTitle>
          </DialogHeader>
          
          {bulkDeleteData && (
            <div className="space-y-4 py-4">
              {/* Summary Info */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2 text-orange-800">
                  <Users className="w-5 h-5" />
                  <span className="font-bold">Kelas: {bulkDeleteData.className}</span>
                </div>
                <div className="flex items-center gap-2 text-orange-700">
                  <Utensils className="w-4 h-4" />
                  <span><strong>Jenis Menu:</strong> {bulkDeleteData.mealType}</span>
                </div>
                <div className="flex items-center gap-2 text-orange-700">
                  <Calendar className="w-4 h-4" />
                  <span><strong>Tarikh:</strong> {bulkDeleteData.date}</span>
                </div>
              </div>
              
              {/* Children List */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="font-bold text-red-800 mb-2">
                  ⚠️ {bulkDeleteData.logs.length} rekod akan dipadam:
                </p>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {bulkDeleteData.childrenNames.map((name, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-red-700 text-sm">
                      <span>•</span>
                      <span>{name}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Warning */}
              <p className="text-sm text-gray-600 text-center italic">
                Tindakan ini tidak boleh dibatalkan!
              </p>
              
              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowBulkDeleteDialog(false);
                    setBulkDeleteData(null);
                  }}
                  disabled={bulkDeleting}
                >
                  Batal
                </Button>
                <Button
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                  onClick={executeBulkDelete}
                  disabled={bulkDeleting}
                >
                  {bulkDeleting ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Memadam...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Ya, Padam {bulkDeleteData.logs.length} Rekod
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Image Viewer Modal */}
      <Dialog open={!!viewImageUrl} onOpenChange={(open) => {
        if (!open) setViewImageUrl(null);
      }}>
        <DialogContent className="max-w-3xl p-2">
          <DialogHeader>
            <DialogTitle className="text-center">Gambar Makanan</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center p-4 space-y-4">
            {viewImageUrl && (
              <>
                <img
                  src={optimizeCloudinaryUrl(viewImageUrl)}
                  alt="Food"
                  className="max-w-full max-h-[60vh] object-contain rounded-xl shadow-lg"
                  loading="lazy"
                />
                <Button
                  data-testid="download-menu-image"
                  onClick={() => downloadImage(viewImageUrl, "menu")}
                  className="bg-gradient-to-r from-green-400 to-emerald-500 hover:from-green-500 hover:to-emerald-600 text-white font-bold px-6 py-2 rounded-xl shadow-md"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Muat Turun Gambar
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Duplicate Menu Dialog */}
      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="w-5 h-5 text-green-600" />
              Duplicate Rekod Pemakanan
            </DialogTitle>
          </DialogHeader>
          
          {duplicateMenu && (
            <div className="space-y-4">
              {/* Menu Info */}
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <p className="font-semibold text-gray-800">{duplicateMenu.meal_type}</p>
                <p className="text-gray-600">{duplicateMenu.food_items?.substring(0, 100)}</p>
                <p className="text-gray-500 text-xs mt-1">
                  {duplicateMenu.date}{duplicateMenu.quantity ? ` • Air: ${duplicateMenu.quantity}` : ""}
                </p>
              </div>

              {/* Duplicate Mode */}
              <div className="space-y-3">
                <label className="font-semibold text-sm">Pilih Mod Duplicate:</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setDuplicateMode("individual")}
                    className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                      duplicateMode === "individual"
                        ? "bg-green-500 text-white border-green-500"
                        : "bg-white text-gray-700 border-gray-300 hover:border-green-400"
                    }`}
                  >
                    🧒 Individu Sama
                  </button>
                  <button
                    onClick={() => setDuplicateMode("multiple")}
                    className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                      duplicateMode === "multiple"
                        ? "bg-green-500 text-white border-green-500"
                        : "bg-white text-gray-700 border-gray-300 hover:border-green-400"
                    }`}
                  >
                    👥 Pilih Kanak-kanak
                  </button>
                </div>
              </div>

              {/* Show child selection if multiple mode */}
              {duplicateMode === "multiple" && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="font-semibold text-sm">Pilih Kanak-kanak:</label>
                    <span className="text-xs text-gray-500">
                      {selectedChildrenForDuplicate.length} dipilih
                    </span>
                  </div>
                  
                  {/* Quick select by class */}
                  <div className="flex flex-wrap gap-1">
                    {availableClasses.map(cls => (
                      <button
                        key={cls}
                        onClick={() => selectAllChildrenForDuplicate(cls)}
                        className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full hover:bg-purple-200"
                      >
                        + {cls.split(' ')[0]}
                      </button>
                    ))}
                  </div>
                  
                  {/* Children list */}
                  <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
                    {children.map(child => (
                      <label
                        key={child.id}
                        className={`flex items-center gap-2 p-2 cursor-pointer hover:bg-gray-50 ${
                          selectedChildrenForDuplicate.includes(child.id) ? "bg-green-50" : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedChildrenForDuplicate.includes(child.id)}
                          onChange={() => toggleChildForDuplicate(child.id)}
                          className="rounded text-green-600"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {child.full_name || child.name}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {child.class_name || "Tiada kelas"}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowDuplicateDialog(false)}
                  className="flex-1"
                  disabled={duplicating}
                >
                  Batal
                </Button>
                <Button
                  onClick={handleDuplicateMenu}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  disabled={duplicating || (duplicateMode === "multiple" && selectedChildrenForDuplicate.length === 0)}
                >
                  {duplicating ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Duplicating...
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Duplicate {duplicateMode === "multiple" ? `(${selectedChildrenForDuplicate.length})` : ""}
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Statistics Modal - NEW FEATURE */}
      {showStatsModal && (
        <StatisticsModal
          isOpen={showStatsModal}
          onClose={() => setShowStatsModal(false)}
          statistics={statsData}
          type={statsData?.mealTypeName ? `menu - ${statsData.mealTypeName}` : "menu"}
          className={selectedClass}
          onSelectChild={(child) => {
            setShowStatsModal(false);
            // Open form with selected child
            setFormData({
              child_id: child.id,
              meal_type: "",
              food_items: "",
              quantity: "",
              notes: "",
              time: "",
              date: selectedDate
            });
            setEditingLog(null);
            setShowDialog(true);
          }}
        />
      )}
    </PageLayout>
  );
}
